import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import ReviewForm from "./ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIDGE_URL =
    "https://images.pexels.com/photos/8727416/pexels-photo-8727416.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SUPPORTING = [
    {
        name: "Jamie M.",
        city: "Delray Beach, FL",
        quote:
            "Paul is a hardworking, kind, and friendly human who happens to be an excellent painter. Hired him to paint two rooms in our new house, and we are saving money to have him repaint the rest of the interior. If you're looking for a sign — HIRE HIM.",
    },
    {
        name: "Barry K.",
        city: "Boca Raton, FL",
        quote:
            "Paul is a rare breed — easy to get a hold of, does excellent work at a very reasonable price, and cleans up when he leaves for the day. His experience shows in the final product.",
    },
    {
        name: "Jose A.",
        city: "Palm Beach County, FL",
        quote:
            "We are very satisfied with Paul and his work ethics. Paul made everything simple and comfortable, especially with my family being around. He is the Paint King!",
    },
];

/**
 * Featured testimonial: Midge — plus 3 supporting quotes.
 */
export default function Testimonial() {
    const [community, setCommunity] = useState([]);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/reviews`)
            .then((res) => {
                if (!cancelled && Array.isArray(res.data)) {
                    setCommunity(res.data);
                }
            })
            .catch(() => {
                /* wall is optional; ignore fetch failures */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            id="praise"
            data-testid="praise"
            className="relative bg-black py-24 md:py-40 overflow-hidden"
        >
            <div className="px-6 md:px-16">
                <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 pt-2">
                        [ Ch. 05 ]
                        <br />
                        Praise
                    </div>
                    <h2 className="col-span-12 md:col-span-10 font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight">
                        And then there
                        <br />
                        is <span className="rainbow-text">Midge.</span>
                    </h2>
                </div>

                {/* Midge feature */}
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-stretch">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                        className="col-span-12 md:col-span-5 relative border border-white/15 overflow-hidden group"
                    >
                        <img
                            src={MIDGE_URL}
                            alt="Midge, PaintKing superfan"
                            className="w-full h-full object-cover aspect-[4/5] grayscale group-hover:grayscale-0 transition duration-1000"
                        />
                        <div className="absolute inset-0 rainbow-bg mix-blend-color opacity-40 group-hover:opacity-10 transition-opacity duration-1000 pointer-events-none" />
                        <div className="absolute top-4 left-4 border border-white/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.28em] uppercase backdrop-blur-sm bg-black/30">
                            EXHIBIT · A
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFE600]">
                                Superfan #001
                            </div>
                            <div className="font-display uppercase text-3xl mt-1">
                                Midge
                            </div>
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/60 mt-1">
                                Wellington, FL
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 1,
                            delay: 0.1,
                            ease: [0.19, 1, 0.22, 1],
                        }}
                        className="col-span-12 md:col-span-7 flex flex-col justify-between glass border border-white/15 p-8 md:p-12"
                    >
                        <span className="font-display text-8xl leading-none text-white/10 select-none">
                            “
                        </span>
                        <blockquote className="font-display uppercase text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight -mt-6">
                            I have hired six painters in twenty years. Paul and
                            his crew are the only ones I've ever asked back
                            <span className="rainbow-text">
                                {" "}
                                three times
                            </span>
                            . My walls have never looked this alive. I
                            <span className="rainbow-text"> adore </span>
                            these people.
                        </blockquote>
                        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-6 border-t border-white/10 pt-6">
                            <div>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50">
                                    Signed
                                </div>
                                <div className="font-display uppercase text-2xl mt-1">
                                    Midge — Superfan since Job #43
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <svg
                                        key={i}
                                        viewBox="0 0 24 24"
                                        className="w-5 h-5 fill-[#FFE600]"
                                    >
                                        <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                    </svg>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Credibility strip */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
                    data-testid="reviews-credibility-strip"
                    className="mt-16 border border-white/15 bg-white/[0.02] backdrop-blur-sm px-6 md:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                >
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <svg
                                    key={i}
                                    viewBox="0 0 24 24"
                                    className="w-5 h-5 fill-[#FFE600]"
                                >
                                    <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                </svg>
                            ))}
                        </div>
                        <div className="font-display uppercase text-lg md:text-xl leading-none">
                            5.0 rated
                        </div>
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60 hidden md:block">
                            on Google · 30+ years experience
                        </div>
                    </div>
                    <a
                        href="https://maps.app.goo.gl/1xWkkj92Sce9TqMV8"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="read-google-reviews-link"
                        className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-[#FFE600] border-b border-white/30 hover:border-[#FFE600] pb-1 transition-colors"
                    >
                        Read all reviews →
                    </a>
                </motion.div>

                {/* Supporting quotes — real verified customer reviews */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border border-white/10 divide-y md:divide-y-0 md:divide-x divide-white/10">
                    {SUPPORTING.map((t, i) => (
                        <motion.blockquote
                            key={t.name}
                            data-testid={`quote-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{
                                duration: 0.8,
                                delay: i * 0.1,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="glass p-8 md:p-10 flex flex-col justify-between min-h-[260px] group transition-colors"
                        >
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-0.5">
                                        {Array.from({ length: 5 }).map((_, s) => (
                                            <svg
                                                key={s}
                                                viewBox="0 0 24 24"
                                                className="w-4 h-4 fill-[#FFE600]"
                                            >
                                                <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                            </svg>
                                        ))}
                                    </div>
                                    <span
                                        data-testid={`verified-badge-${i}`}
                                        className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/70 border border-white/25 px-2 py-1"
                                    >
                                        Verified · 5.0
                                    </span>
                                </div>
                                <p className="font-mono text-[14px] leading-relaxed text-white/85">
                                    “{t.quote}”
                                </p>
                            </div>
                            <div className="mt-8">
                                <div className="font-display uppercase text-lg leading-none">
                                    {t.name}
                                </div>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50 mt-1">
                                    {t.city}
                                </div>
                            </div>
                        </motion.blockquote>
                    ))}
                </div>

                {/* Community wall — approved user-submitted reviews */}
                {community.length > 0 && (
                    <div className="mt-10">
                        <div className="flex items-baseline justify-between mb-4">
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                                From the wall · Community
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                                {community.length} review
                                {community.length === 1 ? "" : "s"}
                            </div>
                        </div>
                        <div
                            data-testid="community-wall"
                            className="grid grid-cols-1 md:grid-cols-3 border border-white/10 divide-y md:divide-y-0 md:divide-x divide-white/10"
                        >
                            {community.slice(0, 6).map((r, i) => (
                                <motion.blockquote
                                    key={r.id}
                                    data-testid={`community-quote-${i}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{
                                        duration: 0.8,
                                        delay: i * 0.08,
                                        ease: [0.19, 1, 0.22, 1],
                                    }}
                                    className="glass p-6 md:p-8 flex flex-col justify-between min-h-[220px]"
                                >
                                    <div>
                                        <div className="flex items-center gap-0.5 mb-3">
                                            {Array.from({ length: r.rating }).map((_, s) => (
                                                <svg
                                                    key={s}
                                                    viewBox="0 0 24 24"
                                                    className="w-4 h-4 fill-[#FFE600]"
                                                >
                                                    <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                                </svg>
                                            ))}
                                        </div>
                                        <p className="font-mono text-[13px] leading-relaxed text-white/85">
                                            “{r.quote}”
                                        </p>
                                    </div>
                                    <div className="mt-6">
                                        <div className="font-display uppercase text-base leading-none">
                                            {r.name}
                                        </div>
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50 mt-1">
                                            {r.city}
                                        </div>
                                    </div>
                                </motion.blockquote>
                            ))}
                        </div>
                    </div>
                )}

                {/* Review submission box */}
                <ReviewForm />
            </div>
        </section>
    );
}
